import { UserRole, type UserRoleEnum } from '#shared/enums';
import { useAuth } from '~/composables/useAuth';

export const useRoleAccess = () => {
  const auth = useAuth();
  
  const getCurrentRole = (): UserRoleEnum | null => {
    return auth.getRole();
  };

  const isAdmin = (): boolean => {
    return getCurrentRole() === UserRole.ADMIN;
  };

  const isInspector = (): boolean => {
    return getCurrentRole() === UserRole.INSPECTOR;
  };

  const isSupervisor = (): boolean => {
    return getCurrentRole() === UserRole.SUPERVISOR;
  };

  const isParent = (): boolean => {
    return getCurrentRole() === UserRole.PARENT;
  };

  const isStudent = (): boolean => {
    return getCurrentRole() === UserRole.STUDENT;
  };

  const isDemo = (): boolean => {
    return getCurrentRole() === UserRole.DEMO;
  };

  const isTester = (): boolean => {
    return getCurrentRole() === UserRole.TESTER;
  };

  const canAccessAdministration = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN;
  };

  const canAccessCentre = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.SUPERVISOR;
  };

  const canAccessSessions = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN || role === UserRole.SUPERVISOR;
  };

  const canAccessStatistics = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN || role === UserRole.INSPECTOR;
  };

  const canAccessDrafts = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN || role === UserRole.INSPECTOR || role === UserRole.TESTER;
  };

  const canAccessReport = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN || role === UserRole.SUPERVISOR || role === UserRole.PARENT || role === UserRole.STUDENT;
  };

  const canCreateAccounts = (): boolean => {
    return isAdmin();
  };

  const canEditAccount = (accountRole: UserRoleEnum, _accountId?: number): boolean => {
    const currentRole = getCurrentRole();
    
    if (currentRole === UserRole.ADMIN) {
      return true;
    }
    
    if (currentRole === UserRole.INSPECTOR) {
      return false;
    }
    
    if (currentRole === UserRole.SUPERVISOR) {
      return accountRole === UserRole.STUDENT;
    }
    
    if (currentRole === UserRole.PARENT) {
      return accountRole === UserRole.STUDENT;
    }
    
    return false;
  };

  const canDeleteAccount = (_accountRole: UserRoleEnum): boolean => {
    const currentRole = getCurrentRole();
    
    if (currentRole === UserRole.ADMIN) {
      return true;
    }
    
    return false;
  };

  const canAccessAssessment = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN ||
           role === UserRole.DEMO || 
           role === UserRole.STUDENT;
  };

  const canAccessReports = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN ||
           role === UserRole.DEMO || 
           role === UserRole.STUDENT;
  };

  const canAccessMemos = (): boolean => {
    const role = getCurrentRole();
    return role === UserRole.ADMIN || 
           role === UserRole.INSPECTOR ||
           role === UserRole.TESTER;
  };

  const canViewAccount = (accountRole: UserRoleEnum): boolean => {
    const currentRole = getCurrentRole();
    
    if (currentRole === UserRole.ADMIN) {
      return true;
    }
    
    if (currentRole === UserRole.INSPECTOR) {
      return true;
    }
    
    if (currentRole === UserRole.SUPERVISOR) {
      return accountRole === UserRole.STUDENT;
    }
    
    if (currentRole === UserRole.PARENT) {
      return accountRole === UserRole.STUDENT;
    }
    
    return false;
  };

  const getVisibleTabs = (): string[] => {
    const currentRole = getCurrentRole();
    
    if (currentRole === UserRole.ADMIN) {
      return ['accounts', 'centres'];
    }
    
    return [];
  };

  return {
    getCurrentRole,
    isAdmin,
    isInspector,
    isSupervisor,
    isParent,
    isStudent,
    isDemo,
    isTester,
    canAccessAdministration,
    canAccessCentre,
    canAccessSessions,
    canAccessStatistics,
    canAccessDrafts,
    canAccessReport,
    canAccessAssessment,
    canAccessReports,
    canAccessMemos,
    canCreateAccounts,
    canEditAccount,
    canDeleteAccount,
    canViewAccount,
    getVisibleTabs
  };
};